import React, { useState, useEffect } from "react";
import { Modal, Input, Form } from "antd";
import {
  getAlert,
  insertData,
  getDataWithComp,
} from "../../../utils/SamuiUtils";

export default function AddWarehouseModal({
  open,
  onClose,
  onSuccess,
  existingWarehouses = [],
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const validateWHCode = (_, value) => {
    if (!value) return Promise.reject(new Error("กรุณากรอกรหัสคลัง"));

    // ตรวจสอบว่าเป็นภาษาอังกฤษ ตัวเลข และเครื่องหมาย - _ เท่านั้น
    const englishOnly = /^[A-Za-z0-9-_]+$/;
    if (!englishOnly.test(value)) {
      return Promise.reject(
        new Error("รหัสคลังต้องเป็นภาษาอังกฤษ ตัวเลข และเครื่องหมาย - _ เท่านั้น")
      );
    }

    const isDuplicate = existingWarehouses.some(
      (w) =>
        String(w.code || "").toUpperCase() === String(value || "").toUpperCase()
    );
    if (isDuplicate) {
      return Promise.reject(new Error("รหัสคลังนี้มีอยู่ในระบบแล้ว"));
    }

    return Promise.resolve();
  };

  const validateWHName = (_, value) => {
    if (!value) return Promise.reject(new Error("กรุณากรอกชื่อคลัง"));

    const isDuplicate = existingWarehouses.some(
      (w) =>
        String(w.name || "").toLowerCase() === String(value || "").toLowerCase()
    );
    if (isDuplicate) {
      return Promise.reject(new Error("ชื่อคลังนี้มีอยู่ในระบบแล้ว"));
    }

    return Promise.resolve();
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      setLoading(true);

      const compId = localStorage.getItem("company");

      // ✅ แปลงรหัสคลังเป็นตัวพิมพ์ใหญ่
      const whCodeUpper = values.WH_Code.trim().toUpperCase();

      // ✅ ตรวจสอบซ้ำกับ DB
      let codeCheck, nameCheck;
      try {
        codeCheck = await getDataWithComp(
          "Tb_Set_WH",
          `AND WH_Code='${whCodeUpper}' AND WH_Status='Y'`
        );
      } catch (e) {
        console.error("❌ Error checking WH_Code:", e);
        getAlert("FAILED", "ไม่สามารถตรวจสอบข้อมูลซ้ำได้ กรุณาลองใหม่อีกครั้ง");
        setLoading(false);
        return;
      }

      if (codeCheck && codeCheck.length > 0) {
        getAlert("FAILED", `รหัสคลัง "${whCodeUpper}" มีอยู่ในระบบแล้ว`);
        setLoading(false);
        return;
      }

      try {
        nameCheck = await getDataWithComp(
          "Tb_Set_WH",
          `AND WH_Name='${values.WH_Name.trim()}' AND WH_Status='Y'`
        );
      } catch (e) {
        console.error("❌ Error checking WH_Name:", e);
        getAlert("FAILED", "ไม่สามารถตรวจสอบข้อมูลซ้ำได้ กรุณาลองใหม่อีกครั้ง");
        setLoading(false);
        return;
      }

      // สร้าง fields และ values สำหรับ insert
      const fields = "WH_Code,WH_Name,WH_Status,Comp_Id,Flag_Vansale";
      const valuesArray = [
        whCodeUpper,
        values.WH_Name.trim(),
        "Y",
        Number(compId),
        "N",
      ];

      const response = await insertData(
        "Tb_Set_WH",
        fields,
        valuesArray
      );

      if (response && response.ok !== false) {
        const newWHId = response.data?.[0]?.WH_Id || response.insertId || response.result?.recordset?.[0]?.WH_Id;

        // ✅ รอให้ onSuccess ทำงานเสร็จก่อนปิด modal
        await onSuccess?.(newWHId);

        getAlert("OK", "เพิ่มคลังสำเร็จ");
        onClose();
      } else {
        console.error("❌ Insert warehouse failed:", response);
        const errorMsg =
          response?.error || response?.message || "ไม่สามารถเพิ่มคลังได้";
        getAlert("FAILED", `เกิดข้อผิดพลาด: ${errorMsg}`);
      }
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      console.error("❌ Error adding warehouse:", error);
      const errorMsg =
        error?.response?.data?.error ||
        error?.message ||
        "เกิดข้อผิดพลาดในการเชื่อมต่อกับเซิร์ฟเวอร์";
      getAlert("FAILED", `เกิดข้อผิดพลาด: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title="เพิ่มคลังใหม่"
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="บันทึก"
      cancelText="ยกเลิก"
      width={480}
      destroyOnClose
      zIndex={10600}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="WH_Code"
          label="รหัสคลัง (ภาษาอังกฤษ)"
          rules={[{ validator: validateWHCode }]}
          extra={
            <span style={{ color: "#E13F57", fontSize: "12px" }}>
              ⚠️มีผลกับการจัดการสินค้า
            </span>
          }
        >
          <Input
            placeholder="เช่น WH01, MAIN, STORE"
            maxLength={20}
            style={{ textTransform: "uppercase", height: "35px" }}
          />
        </Form.Item>

        <Form.Item
          name="WH_Name"
          label="ชื่อคลัง"
          rules={[{ validator: validateWHName }]}
        >
          <Input
            placeholder="เช่น คลังหลัก, คลังสาขา"
            maxLength={50}
            size="large"
            style={{ height: "35px" }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
